import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import TodayView from "../components/dashboard/TodayView";
import ServicesView from "../components/dashboard/ServicesView";
import VehicleDataReports from "../components/dashboard/VehicleDataReports";
import SlaScoreboard from "../components/mfcwl/SlaScoreboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Target, Wrench, AlertTriangle, ShieldCheck, LineChart } from "lucide-react";
import { adminApi } from "@/services/api";
import { useAuth } from "../contexts/AuthContext";

// Roles allowed to see (and hit) the unmatched-payments admin endpoint — must
// mirror the backend allow-list so non-admins never fire a 403.
const PAYMENT_ADMIN_ROLES = new Set(["CEO", "CTO", "CTOO", "COUNTRY_HEAD", "HR_HEAD", "SALES_HEAD"]);

// The Reports tab is the data-vendor board: provider chains, what each lookup
// costs and the prepaid wallets. The CEO asked (17 Oct 2026) that it stay
// visible to him alone — not to the other founder. Mirrors the backend's
// `_require_reports_owner` allow-list, so nobody sees a tab that can only 403.
const REPORTS_EMAILS = new Set(["kalyan@wisedrive.com"]);

/**
 * Small banner surfacing Razorpay payments the centralized sync could not
 * auto-match to a lead, so ops spots orphaned money without opening the admin
 * page. Admin-only, hidden when zero, refreshes every 2 min.
 */
function UnmatchedPaymentsBanner() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const isAdmin = PAYMENT_ADMIN_ROLES.has((user?.role_code || "").toUpperCase());

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await adminApi.unmatchedPayments("pending", 200);
        if (alive) setCount(res.data?.count || 0);
      } catch {
        /* endpoint absent (UAT) or not permitted → stay hidden */
      }
    };
    load();
    const t = setInterval(load, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdmin]);

  if (!isAdmin || count < 1) return null;

  return (
    <Link
      to="/admin/payment-reconcile"
      data-testid="unmatched-payments-banner"
      className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 transition-colors hover:bg-amber-100"
    >
      <span className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span data-testid="unmatched-payments-badge-count">
          {count} unmatched {count === 1 ? "payment" : "payments"}
        </span>
        <span className="hidden sm:inline font-normal text-amber-700">
          — money received on Razorpay that couldn't be auto-matched to a lead
        </span>
      </span>
      <span className="whitespace-nowrap text-xs font-semibold underline underline-offset-2">
        Review &amp; map →
      </span>
    </Link>
  );
}

/**
 * Dashboard — four tabs:
 *   • Leads    → existing TodayView (inspection-side, untouched)
 *   • Services → ServicesView (db.service_leads + ad spend)
 *   • MFCWL    → Monthly SLA scoreboard (fine exposure + Tier risk)
 *   • Reports  → vehicle-data providers: wallets + what RC/challan lookups cost
 *                (founders only — the endpoints behind it are founder-gated)
 *
 * Tab selection is mirrored to `?tab=leads|services|mfcwl|reports` so it survives
 * page refresh and can be deep-linked.
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const canViewReports = REPORTS_EMAILS.has((user?.email || "").trim().toLowerCase());
  const [searchParams, setSearchParams] = useSearchParams();
  const qtab = searchParams.get("tab");
  const initial = qtab === "services" ? "services"
    : qtab === "mfcwl" ? "mfcwl"
      : qtab === "reports" && canViewReports ? "reports"
        : "leads";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const current = searchParams.get("tab");
    if (current !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <UnmatchedPaymentsBanner />
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-6 bg-white border border-slate-200" data-testid="dashboard-tabs">
            <TabsTrigger value="leads" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="dashboard-tab-leads">
              <Target className="w-4 h-4 mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="dashboard-tab-services">
              <Wrench className="w-4 h-4 mr-2" /> Services
            </TabsTrigger>
            <TabsTrigger value="mfcwl" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="dashboard-tab-mfcwl">
              <ShieldCheck className="w-4 h-4 mr-2" /> MFCWL
            </TabsTrigger>
            {canViewReports && (
              <TabsTrigger value="reports" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="dashboard-tab-reports">
                <LineChart className="w-4 h-4 mr-2" /> Reports
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="leads" forceMount hidden={tab !== "leads"}>
            <TodayView />
          </TabsContent>

          <TabsContent value="services" forceMount={false}>
            <ServicesView />
          </TabsContent>

          <TabsContent value="mfcwl" forceMount={false}>
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">MFCWL — SLA & penalty exposure</h2>
                <p className="text-sm text-slate-500">Monthly SOW-SLA scoreboard: breaches, near-misses and Tier-escalation risk.</p>
              </div>
              <SlaScoreboard defaultOpen />
            </div>
          </TabsContent>

          {canViewReports && (
            <TabsContent value="reports" forceMount={false}>
              <VehicleDataReports />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
